import React from 'react';
import Button from './Button';
import type { ButtonVariant } from './buttonStyles';

export type ActionKind = 'approve' | 'reject' | 'return' | 'neutral';

const ICONS: Record<ActionKind, React.ReactNode> = {
  approve: <path d="M20 6L9 17l-5-5" />,
  reject: <path d="M18 6L6 18M6 6l12 12" />,
  return: <path d="M9 14L4 9l5-5M4 9h11a5 5 0 0 1 0 10h-1" />,
  neutral: <path d="M5 12h14" />,
};

// Approve is the "happy path" → solid. Reject/Return keep their semantic colour
// but render as outline so they don't compete with the primary action.
const SOLID: Record<ActionKind, ButtonVariant> = {
  approve: 'success',
  reject: 'danger',
  return: 'warning',
  neutral: 'primary',
};

const OUTLINE: Record<ActionKind, ButtonVariant> = {
  approve: 'success-outline',
  reject: 'danger-outline',
  return: 'warning-outline',
  neutral: 'secondary',
};

interface Props {
  kind: ActionKind;
  onClick: () => void;
  children: React.ReactNode;
  variant?: 'solid' | 'outline';
  size?: 'sm' | 'md';
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  className?: string;
}

/**
 * Botão de decisão do fluxo (aprovar, reprovar, devolver): um `Button` com o ícone
 * da intenção. Cor, altura e foco vêm do `Button`; aqui só se escolhe a variante.
 */
const ActionButton: React.FC<Props> = ({
  kind, onClick, children, variant = 'solid', size = 'sm', loading = false, disabled = false, fullWidth = false, className = '',
}) => (
  <Button
    variant={(variant === 'outline' ? OUTLINE : SOLID)[kind]}
    size={size}
    onClick={onClick}
    loading={loading}
    disabled={disabled}
    fullWidth={fullWidth}
    className={className}
  >
    {!loading && (
      <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" className={size === 'md' ? 'h-4 w-4' : 'h-3.5 w-3.5'}>
        {ICONS[kind]}
      </svg>
    )}
    {children}
  </Button>
);

export default ActionButton;
